import { useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { jwtDecode } from 'jwt-decode';
import { useToast } from '@/hooks/useToast';
import { trackSignOut } from '@/utils/analytics';
import { type TierName, type TierLimits, getTierLimits } from '@/lib/tierLimits';
import { AuthContext, type StripeSubscriptionStatus } from './auth-context';

// Response type from get-subscription-status endpoint
interface StripeSubscriptionResponse {
  subscriptionTier: TierName;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeProductId: string | null;
  stripeSubscriptionStatus: StripeSubscriptionStatus | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/**
 * Fetches subscription status from Stripe via edge function
 */
async function fetchStripeSubscriptionStatus(): Promise<StripeSubscriptionResponse> {
  const { data, error } = await supabase.functions.invoke('stripe/get-subscription-status', {
    method: 'GET',
  });

  if (error) {
    // Return free tier on error
    return {
      subscriptionTier: 'free',
      stripeCustomerId: null,
      stripeSubscriptionId: null,
      stripeProductId: null,
      stripeSubscriptionStatus: null,
      currentPeriodEnd: null,
      cancelAtPeriodEnd: false,
    };
  }

  return data as StripeSubscriptionResponse;
}

// Type for decoded JWT with custom claims
interface DecodedJwt {
  user_role?: string;
  [key: string]: unknown;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionTier, setSubscriptionTier] = useState<TierName>('free');
  const [stripeProductId, setStripeProductId] = useState<string | null>(null);
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] =
    useState<StripeSubscriptionStatus | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const { toast } = useToast();

  // Compute tier limits based on current subscription tier
  const tierLimits: TierLimits = useMemo(() => getTierLimits(subscriptionTier), [subscriptionTier]);

  // Track which user ID we've fetched subscription for to prevent duplicate calls
  const fetchedForUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Helper to extract user role from JWT
    const extractUserRole = (session: Session | null): string | null => {
      if (!session?.access_token) return null;
      try {
        const decoded = jwtDecode<DecodedJwt>(session.access_token);
        return decoded.user_role ?? null;
      } catch {
        return null;
      }
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setUserRole(extractUserRole(session));
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setUserRole(extractUserRole(session));
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch subscription status from Stripe when user logs in
  useEffect(() => {
    const userId = user?.id;

    // Skip if no user or already fetched for this user
    if (!userId || fetchedForUserIdRef.current === userId) {
      return;
    }

    const updateSubscriptionStatus = async () => {
      // Mark as fetching for this user
      fetchedForUserIdRef.current = userId;

      try {
        // First, check the cached value in the profiles table
        const { data: profile } = await supabase
          .from('profiles')
          .select('subscription_status')
          .eq('id', userId)
          .single();

        // Use cached value initially for faster UI (only if it's a valid new tier name)
        // Note: 'premium' is legacy - we ignore it and wait for fresh Stripe data
        const validTiers: TierName[] = ['free', 'citizen', 'professional'];
        if (
          profile?.subscription_status &&
          validTiers.includes(profile.subscription_status as TierName)
        ) {
          setSubscriptionTier(profile.subscription_status as TierName);
        }

        // Then fetch fresh status from Stripe (this is the source of truth)
        const stripeStatus = await fetchStripeSubscriptionStatus();
        setSubscriptionTier(stripeStatus.subscriptionTier);
        setStripeProductId(stripeStatus.stripeProductId);
        setStripeSubscriptionStatus(stripeStatus.stripeSubscriptionStatus);

        // Update the cached value in profiles if it differs
        if (profile?.subscription_status !== stripeStatus.subscriptionTier) {
          await supabase
            .from('profiles')
            .update({
              subscription_status: stripeStatus.subscriptionTier,
              stripe_customer_id: stripeStatus.stripeCustomerId,
              stripe_subscription_id: stripeStatus.stripeSubscriptionId,
              stripe_subscription_status: stripeStatus.stripeSubscriptionStatus,
            })
            .eq('id', userId);
        }
      } catch {
        // Reset ref on error so we can retry
        fetchedForUserIdRef.current = null;
      }
    };

    updateSubscriptionStatus();
  }, [user?.id]);

  /**
   * Refreshes subscription status from Stripe
   * Call this after checkout success or when user returns from Stripe portal
   */
  const refreshSubscriptionStatus = useCallback(async (): Promise<TierName> => {
    const userId = user?.id;
    if (!userId) return 'free';

    setSubscriptionLoading(true);
    try {
      const stripeStatus = await fetchStripeSubscriptionStatus();
      setSubscriptionTier(stripeStatus.subscriptionTier);
      setStripeProductId(stripeStatus.stripeProductId);
      setStripeSubscriptionStatus(stripeStatus.stripeSubscriptionStatus);

      // Update the cached value in profiles
      await supabase
        .from('profiles')
        .update({
          subscription_status: stripeStatus.subscriptionTier,
          stripe_customer_id: stripeStatus.stripeCustomerId,
          stripe_subscription_id: stripeStatus.stripeSubscriptionId,
          stripe_subscription_status: stripeStatus.stripeSubscriptionStatus,
        })
        .eq('id', userId);

      return stripeStatus.subscriptionTier;
    } catch {
      return subscriptionTier;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user?.id, subscriptionTier]);

  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      setSubscriptionTier('free');
      setStripeProductId(null);
      setStripeSubscriptionStatus(null);
      // Reset the fetch ref so we fetch again on next login
      fetchedForUserIdRef.current = null;

      // Track sign-out event
      trackSignOut();

      toast({
        title: 'Signed out successfully',
      });
    } catch {
      toast({
        title: 'Error signing out',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Helper method for access control
  const hasAccess = useCallback(
    (requiredTier: 'unauthenticated' | 'authenticated' | 'paid') => {
      if (requiredTier === 'unauthenticated') return true;
      if (requiredTier === 'authenticated') return !!user;
      if (requiredTier === 'paid') return subscriptionTier !== 'free';
      return false;
    },
    [user, subscriptionTier]
  );

  // Determine if user is on a paid tier (citizen or professional)
  const isPaidUser = !!user && subscriptionTier !== 'free';

  const value = useMemo(
    () => ({
      session,
      user,
      loading,
      subscriptionTier,
      tierLimits,
      stripeProductId,
      stripeSubscriptionStatus,
      subscriptionLoading,
      signOut,
      hasAccess,
      refreshSubscriptionStatus,
      isAuthenticated: !!user,
      isPaidUser,
      isAdmin: userRole === 'admin',
    }),
    [
      session,
      user,
      loading,
      subscriptionTier,
      tierLimits,
      stripeProductId,
      stripeSubscriptionStatus,
      subscriptionLoading,
      signOut,
      hasAccess,
      refreshSubscriptionStatus,
      isPaidUser,
      userRole,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
